<template>
  <div>
    <div class='flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-end'>
      <div v-if='slots.title || slots.description'>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <ReportKey v-if='showKey' />
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' style='padding-top: 1px;' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view'>
        <div class='flex flex-wrap -mx-4 -my-8'>
          <div class='px-4 py-8 w-1/2 sm:w-1/3' v-for='group in view.groups'>
            <h3><span class='px-3 py-1 rounded-md border inline-block text-sm text-jananavy-900 font-medium'>{{ group.header.name }}</span></h3>
            <div class='mt-4'>
              <Bar :data='group.bar' :options='barOptions' :style='group.height' />
            </div>
          </div>
        </div>
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, watch, useSlots } from 'vue';

import Chart from 'chart.js/auto';
import { Bar } from 'vue-chartjs';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import ReportKey from '@/components/report/report-key.vue';

import { scoreMap } from '@/kernel/scoring';
import { usePayloadRequest } from '@/kernel/api';
import { debounce } from '@/kernel/debounce';

const props = defineProps({
  params: Object,
  showKey: Boolean
});

const slots = useSlots();

const request = usePayloadRequest('GET', '/assessment-response/historical-aggregate');

const barOptions = {
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${context.parsed.x.toFixed(2)}%`;
        }
      }
    }
  },
  indexAxis: 'y',
  scales: {
    x: {
      stacked: true,
      max: 100,
      ticks: {
        callback: (value, index, ticks) => {
          return `${value}%`;
        }
      }
    },
    y: {
      stacked: true,
      ticks: {
        autoSkip: false
      }
    }
  },
  maintainAspectRatio: false
}

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {

  const data = request.getData();

  if (!data?.length) {
    return null;
  }

  const groups = data.map((group) => {
    const names = [];

    const scores = {
      '0': [],
      '-1': [],
      '-2': [],
      '-3': [],
      '-100': []
    }

    const years = Object.values(group.years).toSorted((a, b) => {
      return b.header.name.toString().localeCompare(a.header.name.toString());
    });

    years.forEach((year) => {
      const total = Object.values(year.scores).reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0);

      names.push(year.header.name);

      scores['0'].push(year.scores['0'] / total * 100);
      scores['-1'].push(year.scores['-1'] / total * 100);
      scores['-2'].push(year.scores['-2'] / total * 100);
      scores['-3'].push(year.scores['-3'] / total * 100);
      scores['-100'].push(year.scores['-100'] / total * 100);
    });

    return {
      ...group,
      height: `height: ${(years.length * 30) + 30}px`,
      bar: {
        labels: names,
        datasets: Object.keys(scores).map((key) => {
          return {
            label: scoreMap.getLabel(key),
            backgroundColor: scoreMap.getColour(key, 'hex'),
            maxBarThickness: 20,
            data: scores[key]
          }
        })
      }
    }
  });

  return { groups }
});

const load = () => {
  if (!props.params) {
    return;
  }

  request.doRequest({
    params: {
      dimension: 'theme',
      ...props.params
    }
  });
}

watch(() => props.params, debounce(() => {
  load();
}), 250);

load();

</script>
